/**
 * Schema definitions for AI Prompt Editor reference panel
 * These schemas document the structure of data sent TO the AI (input)
 * and the JSON structure the AI must return (output).
 *
 * Note: These are read-only reference documentation for prompt authors.
 * The actual schemas are hardcoded in the backend and tightly coupled
 * to TypeScript validation logic.
 */

export const INPUT_SCHEMA = `{
  // ============= CLAIM CONTEXT =============
  // Basic claim information and identifiers
  claimContext: {
    claimNumber: string,           // Claim identifier (e.g., "123456789")
    serviceDate: Date,             // Date of service (ISO format)
    billedChargeAmount: number | null,  // Total billed charges in dollars
    caseSignature: string,         // Unique case identifier
  },

  // ============= PAYER INFORMATION =============
  // Insurance payer details and matching
  payerInfo: {
    claimPayerName: string,        // Payer name from claim (e.g., "AETNA")
    claimPayerGroupNumber: string | null,  // Group/plan number
    networkStatus: string | null,  // In/out of network status
    matchedClearPayerName: string | null,  // Matched payer in our system
    matchedParentPayerName: string | null, // Parent organization name
  },

  // ============= EXPECTED CONTRACT DATA =============
  // Our calculation of what should have been allowed per contract
  expectedContractData: {
    expectedAllowed: number,       // Total expected allowed amount (dollars)

    // Detailed calculation breakdown by CPT code
    calculatedData: {
      cptCodes: [
        {
          code: string,            // CPT/HCPCS code (e.g., "99213", "C1234")
          quantity: number,        // Total quantity (already multiplied)

          // Array of provider procedure calculations
          providerProcedures: [
            {
              // IMPORTANT: finalAmountInCents is in CENTS
              // Always divide by 100 to get dollars
              finalAmountInCents: number,  // Final amount with all adjustments applied (CENTS)
              priceInCents: number,        // Base contract rate (CENTS)
              implantCostInCents: number | null,  // Implant cost if applicable (CENTS)

              // Contract adjustments that were applied
              adjustments: [
                {
                  type: string,    // Adjustment type: "MPDR", "BILATERAL", "Packaged Code", etc.
                  // ... other adjustment fields
                }
              ],

              provider: {
                specialty: string, // Provider specialty (e.g., "FACILITY")
                // ... other provider fields
              }
            }
          ]
        }
      ]
    } | null,

    // Data quality indicators
    dataQualityFlags: {
      isIncomplete: boolean,       // True if calculation is incomplete
      missingCodes: Array<{        // CPT codes without contract rates
        code: string,
        quantity: number
      }>,
      hasAllContractRates: boolean, // True if all codes have rates
      warningMessage: string | null, // Warning if data issues exist
    },

    calculationMethod: string,     // Method used for calculation
  },

  // ============= ACTUAL 835 DATA =============
  // What the payer actually paid (from 835 remittance)
  actual835Data: {
    actualAllowed: number,         // Total allowed by payer (dollars)

    // Line-item details from 835 file
    lineItems: [
      {
        cptCode: string,           // Procedure code
        modifier: string | null,   // Modifier (e.g., "26", "TC", "50")
        revenueCode: string | null, // Revenue code (for facilities)
        lineAllowed: number,       // Allowed amount for this line (dollars)

        // Adjustment codes applied by payer
        adjustments: [
          {
            group: string,         // Group code: "CO" (contractual), "PR" (patient)
            code: string,          // Reason code: "45", "97", "199", etc.
            amount: number         // Adjustment amount (dollars)
          }
        ],

        patientResponsibility: number,  // Patient's share (dollars)
        lineCharge: number        // Amount charged (dollars)
      }
    ] | null,

    dataSource: "835 Electronic Remittance Advice",
  },

  // ============= VARIANCE SUMMARY =============
  // Calculated difference between expected and actual
  varianceSummary: {
    variance: number,              // Expected - Actual (dollars, can be negative)
    percentDifference: number,     // Percentage difference (rounded to 2 decimals)
    isUnderpayment: boolean,       // True if payer paid less than expected
    isOverpayment: boolean,        // True if payer paid more than expected
  },
}`;

export const OUTPUT_SCHEMA = `{
  // High-level summary of the analysis
  "summary": "string - One-paragraph plain-English summary of what happened. Only mention implants if any were identified.",

  // Effective totals — set by the server from the claim's expected and allowed, not written by the model (CLE-2812)
  "effectiveTotals": {
    "effectiveExpectedAllowed": "number - The claim's expected amount, matching the Expected Amounts tab (dollars)",
    "effectiveActualAllowed": "number - The claim's allowed amount (dollars)",
    "underpaymentAmount": "number - effectiveExpectedAllowed - effectiveActualAllowed (dollars)"
  },

  // Optional: Only include if implants were found and excluded
  "excludedImplants": [
    {
      "cpt": "string - CPT/HCPCS code (e.g., 'C1234')",
      "modifier": "string | null - Modifier if applicable",
      "revenueCode": "string - Revenue code that triggered exclusion (e.g., '0272')",
      "expectedAllowed": "number - Expected amount for this implant (dollars)",
      "actualAllowed": "number - Actual allowed for this implant (dollars)",
      "reason": "string - Why this was excluded (e.g., 'HCPCS code starts with C')"
    }
  ],

  // Optional: Line-by-line discrepancy details
  "lineItemDiscrepancies": [
    {
      "cpt": "string - CPT code (e.g., '99213')",
      "modifier": "string | null - Modifier (e.g., '26', 'TC')",
      "expectedAllowed": "number - What we expected (dollars)",
      "actualAllowed": "number - What payer allowed (dollars)",
      "adjustmentCodes": ["string[] - Adjustment codes applied (e.g., 'CO-97', 'CO-199')"],
      "explanation": "string - Detailed explanation of the discrepancy with specific dollar amounts"
    }
  ],

  // Classification codes (max 2, ordered by confidence)
  "classifications": [
    {
      "code": "string - Classification code from taxonomy (e.g., 'C5', 'D2', 'A1')",
      "category": "string - Category name (e.g., 'Contract / Calculation Errors')",
      "title": "string - Issue title (e.g., 'MPPR Error', 'Wrong Fee Schedule Year')",
      "confidence": "enum - 'High' | 'Medium' | 'Low'",
      "explanation": "string - Detailed reason with evidence and line-specific details"
    }
  ],

  // Resolution guidance and appeal draft
  "resolution": {
    "hasRealDiscrepancy": "boolean - true if there's a real underpayment requiring action",
    "appealLetterDraft": "string - Draft appeal letter using the template (or 'No appeal required' if hasRealDiscrepancy=false)"
  }
}
`;
